/**
 * Document-level pull-to-refresh gesture FSM (MOB-02).
 * Scroll root is window/document — not a nested `#main` overflow port.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const PULL_TO_REFRESH_THRESHOLD_PX = 80;

/** Explicit horizontal strip / carousel affinity (PickupSlotPicker day chips). */
export const PTR_HORIZONTAL_IGNORE_ATTR = 'data-customer-ptr-ignore';
export const PTR_HORIZONTAL_IGNORE_VALUE = 'horizontal';

export type PullToRefreshPhase = 'idle' | 'pulling' | 'release' | 'refreshing';

export interface UsePullToRefreshOptions {
  /** Invoked when the user releases past the threshold. */
  readonly onRefresh: () => Promise<void>;
  /** Vertical pull distance required to arm refresh (default 80). */
  readonly thresholdPx?: number;
  /** Top-of-stack overlay dismiss; non-null disables the gesture. */
  readonly overlayDismiss: (() => void) | null;
  /** Optional extra gate — return true to ignore the gesture. */
  readonly isDisabled?: () => boolean;
}

export interface UsePullToRefreshResult {
  readonly phase: PullToRefreshPhase;
  /** Current pull distance in px while tracking (0 when idle/refreshing). */
  readonly pullDistance: number;
  /** True once pull distance has crossed the threshold this gesture. */
  readonly pastThreshold: boolean;
}

type GestureMode = 'idle' | 'pending' | 'armed' | 'aborted';

function readScrollY(): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function isOverflowScrollableX(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  const overflowX = style.overflowX;
  if (overflowX !== 'auto' && overflowX !== 'scroll' && overflowX !== 'overlay') {
    return false;
  }
  return el.scrollWidth > el.clientWidth + 1;
}

function isOverflowScrollableY(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
    return false;
  }
  return el.scrollHeight > el.clientHeight + 1;
}

/**
 * G16: do not arm PTR when the touch began inside a horizontal carousel/strip
 * or a nested vertical scroll pane that is not at its top.
 */
export function isPullStartBlockedByTarget(target: EventTarget | null): boolean {
  if (typeof document === 'undefined' || !(target instanceof Element)) {
    return false;
  }

  const markedHorizontal = target.closest(
    `[${PTR_HORIZONTAL_IGNORE_ATTR}="${PTR_HORIZONTAL_IGNORE_VALUE}"]`,
  );
  if (markedHorizontal !== null) {
    return true;
  }

  let el: Element | null = target;
  while (el !== null && el !== document.documentElement && el !== document.body) {
    if (el instanceof HTMLElement) {
      if (isOverflowScrollableX(el)) {
        return true;
      }
      if (isOverflowScrollableY(el) && el.scrollTop > 1) {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Touch / pointer pull-to-refresh. Listeners attach to `window`.
 * `preventDefault` runs only while the gesture is armed (passive: false).
 */
export function usePullToRefresh(options: UsePullToRefreshOptions): UsePullToRefreshResult {
  const {
    onRefresh,
    thresholdPx = PULL_TO_REFRESH_THRESHOLD_PX,
    overlayDismiss,
    isDisabled,
  } = options;

  const [phase, setPhase] = useState<PullToRefreshPhase>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const [pastThreshold, setPastThreshold] = useState(false);

  const overlayDismissRef = useRef(overlayDismiss);
  const isDisabledRef = useRef(isDisabled);
  const onRefreshRef = useRef(onRefresh);
  const thresholdRef = useRef(thresholdPx);

  useEffect(() => {
    overlayDismissRef.current = overlayDismiss;
    isDisabledRef.current = isDisabled;
    onRefreshRef.current = onRefresh;
    thresholdRef.current = thresholdPx;
  }, [overlayDismiss, isDisabled, onRefresh, thresholdPx]);

  const modeRef = useRef<GestureMode>('idle');
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const pullDistanceRef = useRef(0);
  const inFlightRef = useRef(false);

  const resetGesture = useCallback((): void => {
    modeRef.current = 'idle';
    pullDistanceRef.current = 0;
    setPullDistance(0);
    setPastThreshold(false);
    if (!inFlightRef.current) {
      setPhase('idle');
    }
  }, []);

  const canStartGesture = useCallback((): boolean => {
    if (inFlightRef.current) {
      return false;
    }
    if (overlayDismissRef.current !== null) {
      return false;
    }
    if (isDisabledRef.current?.() === true) {
      return false;
    }
    if (readScrollY() > 1) {
      return false;
    }
    return true;
  }, []);

  useEffect(() => {
    const onTouchStart = (event: TouchEvent): void => {
      if (event.touches.length !== 1) {
        modeRef.current = 'aborted';
        return;
      }
      if (!canStartGesture()) {
        modeRef.current = 'idle';
        return;
      }
      if (isPullStartBlockedByTarget(event.target)) {
        modeRef.current = 'idle';
        return;
      }
      const touch = event.touches[0];
      if (touch === undefined) {
        return;
      }
      modeRef.current = 'pending';
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      pullDistanceRef.current = 0;
      setPullDistance(0);
      setPastThreshold(false);
      setPhase('idle');
    };

    const onTouchMove = (event: TouchEvent): void => {
      const mode = modeRef.current;
      if (mode === 'idle' || mode === 'aborted') {
        return;
      }
      if (inFlightRef.current) {
        return;
      }
      if (overlayDismissRef.current !== null || isDisabledRef.current?.() === true) {
        modeRef.current = 'aborted';
        resetGesture();
        return;
      }

      const touch = event.touches[0];
      if (touch === undefined) {
        return;
      }
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      if (mode === 'pending') {
        if (Math.abs(dx) > Math.abs(dy)) {
          modeRef.current = 'aborted';
          resetGesture();
          return;
        }
        if (dy <= 0) {
          return;
        }
        if (readScrollY() > 1) {
          modeRef.current = 'aborted';
          resetGesture();
          return;
        }
        modeRef.current = 'armed';
      }

      if (modeRef.current !== 'armed') {
        return;
      }

      if (readScrollY() > 1) {
        modeRef.current = 'aborted';
        resetGesture();
        return;
      }

      if (dy <= 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        setPastThreshold(false);
        setPhase('idle');
        return;
      }

      // Armed PTR — block native overscroll rubber-band while we own the gesture.
      if (event.cancelable) {
        event.preventDefault();
      }

      const distance = dy;
      pullDistanceRef.current = distance;
      setPullDistance(distance);
      const crossed = distance >= thresholdRef.current;
      setPastThreshold(crossed);
      setPhase(crossed ? 'release' : 'pulling');
    };

    const finishGesture = (): void => {
      const mode = modeRef.current;
      const distance = pullDistanceRef.current;
      const threshold = thresholdRef.current;

      if (mode !== 'armed' || inFlightRef.current) {
        resetGesture();
        return;
      }

      if (distance < threshold) {
        resetGesture();
        return;
      }

      inFlightRef.current = true;
      modeRef.current = 'idle';
      pullDistanceRef.current = 0;
      setPullDistance(0);
      setPastThreshold(true);
      setPhase('refreshing');

      void (async (): Promise<void> => {
        try {
          await onRefreshRef.current();
        } finally {
          inFlightRef.current = false;
          setPastThreshold(false);
          setPhase('idle');
        }
      })();
    };

    const onTouchEnd = (): void => {
      if (modeRef.current === 'pending' || modeRef.current === 'aborted') {
        resetGesture();
        return;
      }
      finishGesture();
    };

    const onTouchCancel = (): void => {
      resetGesture();
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return (): void => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [canStartGesture, resetGesture]);

  return { phase, pullDistance, pastThreshold };
}
